import { useEffect } from "react";
import type { ReactNode } from "react";
import { EntityMeta } from "./EntityMeta";
import { IconEdit, IconTrash } from "./icons";
import type { BadgeTone } from "../utils/status";
import { useAppDispatch } from "../redux/store/hooks";
import { setBreadcrumbDetail } from "../redux/slice/layoutSlice";

interface EntityOverviewProps {
  /** Label for the "back to list" breadcrumb link, e.g. "Products". */
  backLabel: string;
  onBack: () => void;
  title: string;
  id?: string;
  status?: { label: string; tone: BadgeTone };
  /** Extra header content under the title (e.g. price, SKU) — plain text/badges. */
  subtitle?: ReactNode;
  onEdit?: () => void;
  onDelete?: () => void;
  /** Main content — usually an OverviewFieldList, or Product's own tabbed body. */
  children: ReactNode;
  /** Optional right-column content (image, related-records panel, ...). */
  side?: ReactNode;
}

// Shared chrome for every inventory entity's "view" screen — reached from the
// list table's row-click / eye icon. Renders in place of the list (no router
// in this app), with Edit reusing that entity's existing FormModal popup.
// The record's title is pushed up into the app's own top breadcrumb (as a
// third segment, via layoutSlice's `breadcrumbDetail`) instead of repeating it
// in a second, in-page breadcrumb here — going back to the list is the
// sidebar's job (re-clicking the active nav item remounts the screen).
export const EntityOverview = ({
  title,
  id,
  status,
  subtitle,
  onEdit,
  onDelete,
  children,
  side,
}: EntityOverviewProps) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(setBreadcrumbDetail(title));
    return () => {
      dispatch(setBreadcrumbDetail(null));
    };
  }, [dispatch, title]);

  return (
    <div className="overview">
      <div className={`overview__grid${side ? "" : " overview__grid--single"}`}>
        <div className="overview__main panel">
          <div className="panel__head">
            <div>
              <div className="overview__title-row">
                <h2>{title}</h2>
                {status ? (
                  <span className={`badge badge--${status.tone}`}>
                    <span className="badge__dot" />
                    {status.label}
                  </span>
                ) : null}
              </div>
              <EntityMeta id={id} />
              {subtitle ? <div className="overview__subtitle">{subtitle}</div> : null}
            </div>

            <div className="overview__actions">
              {onEdit ? (
                <button type="button" className="btn btn--primary" onClick={onEdit}>
                  <IconEdit size={16} /> Edit
                </button>
              ) : null}
              {onDelete ? (
                <button type="button" className="btn" onClick={onDelete}>
                  <IconTrash size={16} /> Delete
                </button>
              ) : null}
            </div>
          </div>

          <div className="panel__body">{children}</div>
        </div>
        {side ? <div className="overview__side panel">{side}</div> : null}
      </div>
    </div>
  );
};
